/**
 * 权限管理 Hook
 * 用于获取当前用户权限并提供权限检查方法
 */

import { useModel } from '@umijs/max';
import { useEffect, useState } from 'react';
import {
  checkButtonPermission,
  checkMenuPermission,
  getMenuButtonsFromStorage,
  getPermissionsFromStorage,
  type PermissionData,
} from '@/utils/permission';

interface PermissionItem {
  id: number;
  code: string;
  name: string;
  type: 'MODULE' | 'MENU' | 'BUTTON';
  parentId?: number;
  children?: PermissionItem[];
}

interface UsePermissionReturn {
  permissions: PermissionData | null;
  loading: boolean;
  hasPermission: (code: string) => boolean;
  hasButtonPermission: (menuCode: string, buttonCode: string) => boolean;
  hasMenuPermission: (menuCode: string) => boolean;
  hasRowButtons: (menuCode: string) => boolean;
  refreshPermissions: () => Promise<void>;
}

export const usePermission = (): UsePermissionReturn => {
  const { initialState } = useModel('@@initialState');
  const [permissions, setPermissions] = useState<PermissionData | null>(null);
  const [loading, setLoading] = useState(false);

  // 从缓存加载权限
  const loadPermissions = () => {
    const cachedPermissions = getPermissionsFromStorage();
    if (cachedPermissions) {
      setPermissions(cachedPermissions);
    }
  };

  // 检查是否有指定权限码的权限
  const hasPermission = (code: string): boolean => {
    return checkMenuPermission(code) || checkButtonPermission(code, code);
  };

  // 检查是否有指定菜单的按钮权限
  const hasButtonPermission = (
    menuCode: string,
    buttonCode: string,
  ): boolean => {
    return checkButtonPermission(menuCode, buttonCode);
  };

  // 检查是否有菜单权限
  const hasMenuPermission = (menuCode: string): boolean => {
    return checkMenuPermission(menuCode);
  };

  // 检查某个页面是否有 placement 为 row 的按钮权限
  const hasRowButtons = (menuCode: string): boolean => {
    const buttons = getMenuButtonsFromStorage(menuCode);
    if (!buttons || buttons.length === 0) {
      return false;
    }
    return buttons.some((button) => button.placement === 'row');
  };

  // 刷新权限（从缓存重新加载）
  const refreshPermissions = async () => {
    setLoading(true);
    try {
      loadPermissions();
    } finally {
      setLoading(false);
    }
  };

  // 初始化时加载权限
  useEffect(() => {
    if (initialState?.currentUser) {
      loadPermissions();
    }
  }, [initialState?.currentUser]);

  return {
    permissions,
    loading,
    hasPermission,
    hasButtonPermission,
    hasMenuPermission,
    hasRowButtons,
    refreshPermissions,
  };
};
